import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import { andFilterGroups } from "../lib/and-filter-groups";
import {
  ACCESS_UPDATING_MESSAGE,
  accessErrorMessage,
  HIDDEN_RECORD_MESSAGE,
  httpStatusOf,
  listRuleBroken,
} from "../lib/member-access";
import { EXPORT_PAGE_SIZE } from "../lib/csv-export";
import { PHONE_PAGE_SIZE, PHONE_PAGE_SIZES } from "../lib/list-window";
import { getApi } from "../lib/get-api";
import { mapLimitSettled } from "../lib/map-limit";
import { describeWriteError } from "../lib/pg-write-error";
import { rowKey } from "../lib/row-key";
import { buildPayload } from "../lib/validate-record";
import { formatValue } from "../lib/format";
import {
  Field,
  columnRank,
  fieldLabel,
  humanize,
  isReadOnlyField,
  isSecretField,
  isStructured,
  mobileCardFields,
  primaryFieldName,
  rowLabel,
  searchableIdentityNames,
  searchFiltersFor,
  singularize,
} from "../lib/schema-field";
import { useAuth } from "../lib/useAuth";
import { RowDetailModal } from "./RowDetailModal";
import { BulkEditModal } from "./BulkEditModal";
import { ConfirmDeleteModal } from "./ConfirmDeleteModal";
import { FilterChips } from "./FilterChips";
import { Button } from "./ui/Button";

type CollectionSearchProps = {
  datasourceId: string;
  collectionName: string;
  schema: Field[];
  /** Opens the existing collection edit drawer. Guest/read-only omit this. */
  onEdit?: (row: any) => void;
  /**
   * Opens the existing create drawer. Omit for guest/demo/read-only —
   * those surfaces hide New record on desktop too.
   */
  onCreate?: () => void;
  /** Bump after a successful form save so hits refresh without a retype. */
  refreshEpoch?: number;
  demoMode?: boolean;
  /**
   * Same `filterGroups` query param the desktop table uses. Phone does not
   * mount that table, so without this prop a Status/Priority filter set on
   * desktop is invisible here and a phone user cannot set one.
   */
  filterGroups?: string;
  onFilterChange?: (next: string | undefined) => void;
  /**
   * Same `sort` query param the desktop grid writes (`field:asc|desc`).
   * Phone does not mount that grid, so without this the list is stuck on
   * primary-key order and a sort set on a wide window never arrives.
   * Omit (or pass undefined) for primary-key ascending.
   */
  sort?: string;
  onSortChange?: (next: string | undefined) => void;
  /**
   * Same `q` / `page` / `limit` the desktop grid writes. Without these a
   * phone refresh, or a link opened from a wide window, starts at page 1
   * with an empty box. Omit them (tests, a caller with no router) and the
   * list stays in component state.
   */
  urlQuery?: string;
  urlPage?: number;
  urlPageSize?: number;
  onListParamsChange?: (next: { q: string; page: number; pageSize: number }) => void;
  /**
   * Report this list's own total/loading so a sibling heading reads the same
   * count the cards use. The grid's raw-`q` total disagrees on a `#id` key
   * search, so the phone heading must not read the grid's number.
   */
  onResultChange?: (result: {
    total: number;
    loading: boolean;
    /** The API served this answer from the read cache (ARD 9.5). */
    stale?: boolean;
  }) => void;
  /**
   * Member id from the preview URL. Phone search keeps its hits in state, so
   * a change here must load again. Omit it outside a preview.
   */
  previewMemberId?: string | null;
};

function defaultSort(pkName?: string): string | undefined {
  return pkName ? `${pkName}:asc` : undefined;
}

/** URL `sort` wins; a missing param is the same stable order the grid uses. */
function resolveSort(raw: string | undefined, pkName?: string): string | undefined {
  if (raw && raw.includes(":")) return raw;
  return defaultSort(pkName);
}

/** Scalars the API can ORDER BY. JSON and secrets are not a sort key. */
function sortFields(schema: Field[]): Field[] {
  return schema
    .filter((field) => !isStructured(field) && !isSecretField(field))
    .sort((a, b) => {
      const delta = columnRank(a) - columnRank(b);
      return delta !== 0 ? delta : a.name.localeCompare(b.name);
    });
}

const PAGE_SIZE = PHONE_PAGE_SIZE;
/** Same cap as the desktop grid. Local so this file does not import the table hook. */
const BULK_SELECT_CAP = 500;

export const CollectionSearch: React.FC<CollectionSearchProps> = ({
  datasourceId,
  collectionName,
  schema,
  onEdit,
  onCreate,
  refreshEpoch,
  demoMode = false,
  filterGroups,
  onFilterChange,
  sort,
  onSortChange,
  urlQuery,
  urlPage,
  urlPageSize,
  onListParamsChange,
  onResultChange,
  previewMemberId = null,
}) => {
  const { token } = useAuth();
  const api = useMemo(() => getApi({ token }), [token]);
  const pkName = primaryFieldName(schema);
  const identityFields = useMemo(
    () => searchableIdentityNames(schema),
    [schema]
  );
  /**
   * Phone hits carry the same two ranked scalars as MobileRecordList cards.
   * A bare name is not enough to pick a record when several read alike.
   */
  const cardExtras = useMemo(() => mobileCardFields(schema).extras, [schema]);

  const [query, setQuery] = useState(urlQuery ?? "");
  const [page, setPage] = useState(urlPage && urlPage > 0 ? urlPage : 1);
  const [pageSize, setPageSize] = useState(
    urlPageSize && urlPageSize > 0 ? urlPageSize : PAGE_SIZE
  );
  const [pageInput, setPageInput] = useState(String(urlPage && urlPage > 0 ? urlPage : 1));
  const pageInputId = useId();
  const [localSort, setLocalSort] = useState<string | undefined>(sort);
  const explicitSort = onSortChange ? sort : localSort;
  const activeSort = resolveSort(explicitSort, pkName);
  const sortRef = useRef(activeSort);
  sortRef.current = activeSort;
  const [sortField, sortDirRaw] = (activeSort ?? "").split(":");
  const sortDir: "asc" | "desc" = sortDirRaw === "desc" ? "desc" : "asc";
  const [hits, setHits] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accessNotice, setAccessNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<any | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<"edit" | "delete" | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [selectingMatch, setSelectingMatch] = useState(false);
  const busyRef = useRef(false);
  const selectGen = useRef(0);
  const canWrite = !!onEdit;

  const [stale, setStale] = useState(false);

  const onResultChangeRef = useRef(onResultChange);
  onResultChangeRef.current = onResultChange;
  useEffect(() => {
    onResultChangeRef.current?.(
      stale ? { total, loading, stale } : { total, loading }
    );
  }, [total, loading, stale]);

  useEffect(() => {
    selectGen.current += 1;
    setSelectingMatch(false);
    setSelectedIds([]);
    setBulkAction(null);
  }, [query, datasourceId, collectionName, refreshEpoch, canWrite, filterGroups, previewMemberId]);

  const pageRef = useRef(page);
  pageRef.current = page;
  const queryRef = useRef(query);
  queryRef.current = query;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const onListParamsChangeRef = useRef(onListParamsChange);
  onListParamsChangeRef.current = onListParamsChange;
  const filterGroupsRef = useRef(filterGroups);
  filterGroupsRef.current = filterGroups;
  const applyingUrl = useRef(false);
  const previousUrl = useRef({ urlQuery, urlPage, urlPageSize });
  const loadGeneration = useRef(0);

  useEffect(() => () => { loadGeneration.current += 1; }, []);

  const publishList = useCallback(
    (next: { q: string; page: number; pageSize: number }) => {
      onListParamsChangeRef.current?.(next);
    },
    []
  );

  const listQuery = useCallback(
    (raw: string, pageNum: number, limit: number) => {
      const needle = raw.trim();
      // Same rule as the relation picker: a name column must not hide the id.
      const filters = searchFiltersFor(needle, identityFields, pkName);
      const searchGroups = filters
        ? JSON.stringify({ operator: "OR", filters })
        : undefined;
      const effectiveFilters = andFilterGroups(filterGroups, searchGroups);
      return {
        ...(effectiveFilters ? { filterGroups: effectiveFilters } : {}),
        limit,
        page: pageNum,
        sort: sortRef.current,
      };
    },
    [filterGroups, identityFields, pkName]
  );

  const loadHits = useCallback(
    async (raw: string, pageNum: number) => {
      const generation = ++loadGeneration.current;
      if (!datasourceId || !collectionName) {
        setHits([]);
        setTotal(0);
        setAccessNotice(null);
        setLoading(false);
        return;
      }
      const size = pageSizeRef.current;
      setLoading(true);
      setLoadError(null);
      try {
        const response = await api.data.listData(
          datasourceId,
          collectionName,
          listQuery(raw, pageNum, size)
        );
        if (generation !== loadGeneration.current) return;
        const payload = response.data as {
          items?: any[];
          total?: number;
          stale?: boolean;
          rule_broken?: unknown;
        };
        const items = (payload.items as any[]) ?? [];
        setStale(!!payload.stale);
        setAccessNotice(
          listRuleBroken(payload) ? ACCESS_UPDATING_MESSAGE : null,
        );
        const totalCount = payload.total ?? items.length;
        const lastPageNum = Math.max(1, Math.ceil(totalCount / size) || 1);
        if (pageNum > lastPageNum && totalCount > 0) {
          pageRef.current = lastPageNum;
          setPage(lastPageNum);
          publishList({ q: raw, page: lastPageNum, pageSize: size });
          await loadHits(raw, lastPageNum);
          return;
        }
        setHits(items.slice(0, size));
        setTotal(totalCount);
        if (pageRef.current !== pageNum) {
          pageRef.current = pageNum;
          setPage(pageNum);
        }
      } catch (err) {
        if (generation !== loadGeneration.current) return;
        setHits([]);
        setTotal(0);
        setStale(false);
        setAccessNotice(null);
        setLoadError(
          httpStatusOf(err) === 404 ? HIDDEN_RECORD_MESSAGE : null,
        );
      } finally {
        if (generation === loadGeneration.current) setLoading(false);
      }
    },
    [api, collectionName, datasourceId, listQuery, publishList]
  );

  const fetchHits = useMemo(
    () =>
      debounce((raw: string, pageNum: number) => {
        pageRef.current = pageNum;
        setPage(pageNum);
        void loadHits(raw, pageNum);
      }, 300),
    [loadHits]
  );

  // First pass keeps the URL page. A later query or filter change starts over
  // at page 1 and writes that, so a Status filter does not land on an empty
  // page 3. `applyingUrl` is a back/forward sync and must not clear the page
  // it just restored.
  const bootQuery = useRef(query);
  const bootFilters = useRef(filterGroups);
  useEffect(() => {
    if (!onListParamsChange) return;
    const previous = previousUrl.current;
    previousUrl.current = { urlQuery, urlPage, urlPageSize };
    if (
      previous.urlQuery === urlQuery &&
      previous.urlPage === urlPage &&
      previous.urlPageSize === urlPageSize
    ) return;
    const nextQuery = urlQuery ?? "";
    const nextPage = urlPage && urlPage > 0 ? urlPage : 1;
    const nextSize = urlPageSize && urlPageSize > 0 ? urlPageSize : PAGE_SIZE;
    if (
      nextQuery === queryRef.current &&
      nextPage === pageRef.current &&
      nextSize === pageSizeRef.current
    ) {
      return;
    }
    applyingUrl.current =
      nextQuery !== queryRef.current || bootFilters.current !== filterGroupsRef.current;
    bootQuery.current = nextQuery;
    bootFilters.current = filterGroupsRef.current;
    queryRef.current = nextQuery;
    pageRef.current = nextPage;
    pageSizeRef.current = nextSize;
    setQuery(nextQuery);
    setPage(nextPage);
    setPageSize(nextSize);
    fetchHits.cancel();
    void loadHits(nextQuery, nextPage);
  }, [
    urlQuery,
    urlPage,
    urlPageSize,
    onListParamsChange,
    fetchHits,
    loadHits,
  ]);

  useEffect(() => {
    if (applyingUrl.current) {
      applyingUrl.current = query !== bootQuery.current;
      return () => {
        fetchHits.cancel();
      };
    }
    loadGeneration.current += 1;
    const userMoved =
      query !== bootQuery.current || filterGroups !== bootFilters.current;
    const pageNum = userMoved ? 1 : pageRef.current;
    if (userMoved) {
      bootQuery.current = query;
      bootFilters.current = filterGroups;
      pageRef.current = 1;
      setPage(1);
      publishList({ q: query, page: 1, pageSize: pageSizeRef.current });
    }
    fetchHits(query, pageNum);
    return () => {
      fetchHits.cancel();
    };
  }, [fetchHits, query, filterGroups, publishList, previewMemberId]);

  useEffect(() => {
    if (!refreshEpoch) return;
    fetchHits.cancel();
    void loadHits(queryRef.current, pageRef.current);
  }, [refreshEpoch, fetchHits, loadHits]);

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const goPage = useCallback(
    (next: number) => {
      if (next < 1 || next === pageRef.current) return;
      fetchHits.cancel();
      pageRef.current = next;
      setPage(next);
      setPageInput(String(next));
      publishList({
        q: queryRef.current,
        page: next,
        pageSize: pageSizeRef.current,
      });
      void loadHits(queryRef.current, next);
    },
    [fetchHits, loadHits, publishList]
  );

  const onPageSize = useCallback(
    (next: number) => {
      if (!(PHONE_PAGE_SIZES as readonly number[]).includes(next)) return;
      if (next === pageSizeRef.current) return;
      pageSizeRef.current = next;
      setPageSize(next);
      fetchHits.cancel();
      pageRef.current = 1;
      setPage(1);
      publishList({ q: queryRef.current, page: 1, pageSize: next });
      void loadHits(queryRef.current, 1);
    },
    [fetchHits, loadHits, publishList]
  );

  const applySort = useCallback(
    (field: string, dir: "asc" | "desc") => {
      const next =
        pkName && field === pkName && dir === "asc" ? undefined : `${field}:${dir}`;
      const resolved = resolveSort(next, pkName);
      if (resolved === sortRef.current) return;
      sortRef.current = resolved;
      setLocalSort(next);
      onSortChange?.(next);
      fetchHits.cancel();
      pageRef.current = 1;
      setPage(1);
      publishList({ q: queryRef.current, page: 1, pageSize: pageSizeRef.current });
      void loadHits(queryRef.current, 1);
    },
    [fetchHits, loadHits, onSortChange, pkName, publishList]
  );

  const selectMatching = useCallback(async () => {
    if (!onEdit || selectingMatch || busyRef.current) return;
    const gen = ++selectGen.current;
    setSelectingMatch(true);
    try {
      const ids: string[] = [];
      let pageNum = 1;
      while (ids.length < BULK_SELECT_CAP) {
        const response = await api.data.listData(
          datasourceId,
          collectionName,
          listQuery(queryRef.current, pageNum, EXPORT_PAGE_SIZE)
        );
        if (gen !== selectGen.current) return;
        const payload = response.data as { items?: any[]; total?: number };
        const items = payload.items ?? [];
        if (!items.length) break;
        for (const item of items) {
          if (ids.length >= BULK_SELECT_CAP) break;
          const id = rowKey(item, pkName);
          if (id == null) continue;
          ids.push(id);
        }
        if (ids.length >= BULK_SELECT_CAP) break;
        if (items.length < EXPORT_PAGE_SIZE) break;
        if (payload.total && ids.length >= payload.total) break;
        pageNum += 1;
      }
      if (gen !== selectGen.current) return;
      if (!ids.length) {
        toast.error("Could not select matching records");
        return;
      }
      setSelectedIds(ids);
      if (total > BULK_SELECT_CAP) {
        toast.success(
          `Selected first ${ids.length} matching records (capped at ${BULK_SELECT_CAP})`
        );
      }
    } catch (err: any) {
      if (gen !== selectGen.current) return;
      toast.error(
        accessErrorMessage(err, "Could not load matching records")
      );
    } finally {
      if (gen === selectGen.current) setSelectingMatch(false);
    }
  }, [
    api,
    collectionName,
    datasourceId,
    listQuery,
    onEdit,
    pkName,
    selectingMatch,
    total,
  ]);

  const onPick = useCallback((row: any) => {
    setDetail(row);
  }, []);

  const applyBulk = async (action: "edit" | "delete", values: Record<string, any> = {}) => {
    if (!onEdit || busyRef.current || selectedIds.length === 0) return;
    const chosenFields = schema.filter((field) =>
      !isReadOnlyField(field) && Object.prototype.hasOwnProperty.call(values, field.name)
    );
    if (action === "edit" && chosenFields.length === 0) return;
    if (demoMode) {
      toast.success("Demo only. The demo database is not modified.");
      setSelectedIds([]);
      setBulkAction(null);
      return;
    }
    const payload = buildPayload(schema, Object.fromEntries(
      chosenFields.map((field) => [field.name, values[field.name] ?? ""])
    ));
    if (action === "edit" && Object.keys(payload).length === 0) return;
    busyRef.current = true;
    setProgress({ done: 0, total: selectedIds.length });
    try {
      const results = await mapLimitSettled(
        selectedIds,
        4,
        (id) => action === "delete"
          ? api.data.deleteData(datasourceId, collectionName, id)
          : api.data.updateData(datasourceId, collectionName, id, payload),
        (done, total) => setProgress({ done, total })
      );
      const rejected = results.filter((result): result is PromiseRejectedResult => result.status === "rejected");
      const succeeded = results.length - rejected.length;
      if (succeeded) {
        toast.success(`${action === "delete" ? "Deleted" : "Updated"} ${succeeded} ${succeeded === 1 ? "record" : "records"}`);
      }
      if (rejected.length) {
        toast.error(`${rejected.length} ${rejected.length === 1 ? "record" : "records"} could not be ${action === "delete" ? "deleted" : "updated"}. ${describeWriteError(rejected[0].reason, schema).message}`);
      }
      setSelectedIds([]);
      fetchHits.cancel();
      await loadHits(queryRef.current, pageRef.current);
    } finally {
      busyRef.current = false;
      setProgress(null);
      setBulkAction(null);
    }
  };

  const noun = singularize(collectionName);
  const lastPage = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = (page - 1) * pageSize + hits.length;
  const showPager = total > pageSize;
  const commitPageInput = () => {
    const p = parseInt(pageInput, 10);
    if (!isNaN(p) && p >= 1 && p <= lastPage && p !== page) {
      goPage(p);
    } else {
      setPageInput(String(page));
    }
  };
  const keyedHits = hits.filter((row) => rowKey(row, pkName) != null);
  const allVisibleSelected =
    keyedHits.length > 0 &&
    keyedHits.every((row) => selectedIds.includes(rowKey(row, pkName) ?? ""));
  const matchingCovered =
    total > BULK_SELECT_CAP
      ? selectedIds.length >= BULK_SELECT_CAP
      : selectedIds.length >= total;
  const showSelectMatching =
    !!onEdit && allVisibleSelected && total > hits.length && !matchingCovered;
  const selectMatchingLabel =
    total > BULK_SELECT_CAP
      ? `Select first ${BULK_SELECT_CAP} matching records`
      : "Select all matching records";
  const sortOptions = useMemo(() => {
    const ranked = sortFields(schema);
    if (sortField && !ranked.some((field) => field.name === sortField)) {
      return [{ name: sortField } as Field, ...ranked];
    }
    return ranked;
  }, [schema, sortField]);
  const sortValue = sortOptions.some((field) => field.name === sortField)
    ? sortField
    : sortOptions[0]?.name ?? "";

  return (
    <div className="shrink-0 px-3 pb-3">
      {onCreate && (
        <Button
          disabled={!!progress}
          onClick={onCreate}
          variant="secondary" size="md" className="od-new-record mb-2 w-full border-od-primary text-sm font-semibold text-od-primary-text hover:bg-transparent hover:opacity-90"
        >
          New record
        </Button>
      )}
      <label className="block">
        <span className="sr-only">Search {humanize(collectionName)}</span>
        <input
          type="search"
          disabled={!!progress}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${humanize(collectionName)}`}
          autoComplete="off"
          enterKeyHint="search"
          className="h-11 w-full rounded-lg border border-od-interactive-boundary bg-od-surface px-3 text-base text-od-text outline-none placeholder:text-od-muted focus:border-od-primary"
        />
      </label>
      {schema.length > 0 && (
        <div className="mt-2">
          <FilterChips
            schema={schema}
            initialFilters={filterGroups}
            onFilterChange={onFilterChange ?? (() => {})}
            touch
          />
        </div>
      )}
      {onEdit && selectedIds.length > 0 && (
        <div role="toolbar" aria-label="Selected records" className="mt-2 flex flex-wrap gap-2">
          {(["edit", "delete"] as const).map((action) => (
            <Button
              key={action}
              disabled={!!progress}
              onClick={() => setBulkAction(action)}
              variant="secondary" size="md" className="px-3 text-sm"
            >
              {action === "edit" ? "Edit" : "Delete"} {selectedIds.length} {selectedIds.length === 1 ? "record" : "records"}
            </Button>
          ))}
          {showSelectMatching && (
            <Button
              disabled={!!progress || selectingMatch}
              onClick={() => void selectMatching()}
              variant="secondary" size="md" className="px-3 text-sm"
            >
              {selectingMatch ? "Selecting…" : selectMatchingLabel}
            </Button>
          )}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {sortOptions.length > 0 && (
          <>
            <label className="inline-flex min-h-11 min-w-0 items-center gap-2 text-sm text-od-muted">
              <span>Sort</span>
              <select
                aria-label="Sort by"
                value={sortValue}
                disabled={!!progress || selectingMatch}
                onChange={(event) => applySort(event.target.value, sortDir)}
                className="min-h-11 max-w-[11rem] rounded-md border border-od-interactive-boundary bg-od-surface px-2 text-sm text-od-text"
              >
                {sortOptions.map((field) => (
                  <option key={field.name} value={field.name}>
                    {fieldLabel(field)}
                  </option>
                ))}
              </select>
            </label>
            <Button
              aria-label={sortDir === "asc" ? "Sort ascending" : "Sort descending"}
              disabled={!!progress || selectingMatch || !sortValue}
              onClick={() =>
                applySort(sortValue, sortDir === "asc" ? "desc" : "asc")
              }
              variant="secondary" size="md" className="px-3 text-sm font-normal"
            >
              {sortDir === "asc" ? "Ascending" : "Descending"}
            </Button>
          </>
        )}
        <label className="inline-flex min-h-11 items-center gap-2 text-sm text-od-muted">
          <span>Rows</span>
          <select
            aria-label="Rows per page"
            value={pageSize}
            disabled={!!progress || selectingMatch}
            onChange={(event) => onPageSize(Number(event.target.value))}
            className="min-h-11 rounded-md border border-od-interactive-boundary bg-od-surface px-2 text-sm text-od-text"
          >
            {PHONE_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ul className="m-0 mt-2 list-none divide-y divide-od-border overflow-hidden rounded-lg border border-od-border">
        {loading && hits.length === 0 && (
          <li className="px-3 py-3 text-sm text-od-muted" role="status">
            Searching…
          </li>
        )}
        {!loading && loadError && (
          <li className="px-3 py-3 text-sm text-od-muted" role="alert">
            {loadError}
          </li>
        )}
        {!loading && !loadError && accessNotice && (
          <li className="px-3 py-3 text-sm text-od-text" role="status">
            {accessNotice}
          </li>
        )}
        {!loading && !loadError && !accessNotice && hits.length === 0 && (
          <li className="px-3 py-3 text-sm text-od-muted">No matches</li>
        )}
        {!accessNotice && hits.map((row) => (
          <li className="flex items-center" key={String(row?.[pkName] ?? rowLabel(row, { fields: schema, pkName }))}>
            {onEdit && (
              <label className="flex min-h-11 min-w-11 shrink-0 cursor-pointer items-center justify-center">
                <input
                  type="checkbox"
                  aria-label={`Select ${rowLabel(row, { fields: schema, pkName })}`}
                  checked={selectedIds.includes(rowKey(row, pkName) ?? "")}
                  disabled={!!progress || rowKey(row, pkName) == null}
                  onChange={(event) => {
                    const id = rowKey(row, pkName);
                    if (id == null) return;
                    const checked = event.target.checked;
                    setSelectedIds((ids) => checked ? [...ids, id] : ids.filter((value) => value !== id));
                  }}
                />
              </label>
            )}
            <button
              type="button"
              onClick={() => onPick(row)}
              className="flex min-h-11 w-full min-w-0 flex-col justify-center gap-0.5 px-3 py-2 text-left text-sm text-od-text hover:bg-od-surface-2"
            >
              <span className="truncate font-medium">
                {rowLabel(row, { fields: schema, pkName })}
              </span>
              {cardExtras.map((field) => (
                <span
                  key={field.name}
                  className="flex min-w-0 justify-between gap-3 text-xs text-od-muted"
                >
                  <span className="shrink-0">{fieldLabel(field)}</span>
                  <span className="min-w-0 truncate text-od-text">
                    {formatValue(row?.[field.name], field)}
                  </span>
                </span>
              ))}
            </button>
          </li>
        ))}
      </ul>
      {showPager && (
        <nav className="mt-2" aria-label="Search result pages">
          <p className="m-0 mb-2 text-center text-sm text-od-muted" aria-live="polite">
            {from}–{to} of {total}
          </p>
          <div className="flex flex-nowrap items-center justify-center gap-2">
            <Button
              aria-label="First page"
              onClick={() => goPage(1)}
              disabled={page <= 1 || loading || !!progress}
              variant="secondary" size="md" className="min-w-11 px-0 text-lg leading-none"
            >
              <span aria-hidden="true">«</span>
            </Button>
            <Button
              aria-label="Previous page"
              onClick={() => goPage(page - 1)}
              disabled={page <= 1 || loading || !!progress}
              variant="secondary" size="md" className="min-w-11 px-0 text-lg leading-none"
            >
              <span aria-hidden="true">‹</span>
            </Button>
            <div className="flex shrink-0 items-center gap-2">
              <label htmlFor={pageInputId} className="sr-only">
                Page
              </label>
              <input
                id={pageInputId}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={commitPageInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitPageInput();
                  }
                }}
                disabled={loading || !!progress}
                className="min-h-11 w-12 rounded-md border border-od-interactive-boundary bg-od-surface text-center text-sm text-od-text"
              />
              <span className="whitespace-nowrap text-sm text-od-muted">of {lastPage}</span>
            </div>
            <Button
              aria-label="Next page"
              onClick={() => goPage(page + 1)}
              disabled={page >= lastPage || loading || !!progress}
              variant="secondary" size="md" className="min-w-11 px-0 text-lg leading-none"
            >
              <span aria-hidden="true">›</span>
            </Button>
            <Button
              aria-label="Last page"
              onClick={() => goPage(lastPage)}
              disabled={page >= lastPage || loading || !!progress}
              variant="secondary" size="md" className="min-w-11 px-0 text-lg leading-none"
            >
              <span aria-hidden="true">»</span>
            </Button>
          </div>
        </nav>
      )}
      {onEdit && (
        <>
          <ConfirmDeleteModal
            open={bulkAction === "delete"}
            onCancel={() => setBulkAction(null)}
            onConfirm={() => void applyBulk("delete")}
            title={`Delete ${selectedIds.length} ${selectedIds.length === 1 ? "record" : "records"}?`}
            confirmLabel={`Delete ${selectedIds.length}`}
            applying={!!progress}
            progress={progress ? `Deleting ${progress.done} of ${progress.total}` : undefined}
            body={demoMode
              ? "Demo only. The demo database is not modified."
              : `This writes ${selectedIds.length} separate DELETE statements to your database. It cannot be undone from here.`}
          />
          <BulkEditModal
            open={bulkAction === "edit"}
            onCancel={() => setBulkAction(null)}
            onApply={(values) => void applyBulk("edit", values)}
            fields={schema}
            count={selectedIds.length}
            demoMode={demoMode}
            applying={!!progress}
            progress={progress ? `Updating ${progress.done} of ${progress.total}` : undefined}
          />
        </>
      )}
      <RowDetailModal
        open={!!detail}
        onClose={() => setDetail(null)}
        row={detail}
        schema={schema}
        title={noun}
        // MOSA-368 slice 1: the phone path opens the same drawer, so it gets
        // the same History tab.
        datasourceId={datasourceId}
        collection={collectionName}
        rowKey={detail ? rowKey(detail, pkName) ?? null : null}
        actions={
          onEdit ? (
            <Button
              onClick={() => {
                const row = detail;
                setDetail(null);
                if (row) onEdit(row);
              }}
              variant="secondary" size="sm"
            >
              Edit
            </Button>
          ) : undefined
        }
      />
    </div>
  );
};
